/**
 * intelligence.js — API client for the Intelligence portal (Lane 826).
 *
 * Deliberately a SEPARATE module from api/chatCapture.js rather than an
 * extension of it. chatCapture.js is the per-message lifecycle client the chat
 * composer fires inline (confirm / edit / dismiss on a chip that just
 * appeared); this is the cross-channel governance client the Intelligence page
 * uses. They talk to different routers and have different lifetimes, and
 * merging them would couple a page rewrite to the live chat path.
 *
 * Same error contract as chatCapture.js: resolves the parsed JSON body, or
 * throws an Error carrying `.status` and `.detail`. Callers branch on
 * `.status` — 404 (gone, cross-tenant, or the flag is off), 409 (this channel
 * does not support that action), 400 (bad input).
 *
 * Auth: JWT from localStorage('authToken'). The global 401 interceptor in
 * authInterceptor.js owns session expiry; we do not branch on 401 here.
 */

import { API_BASE } from '../config';


function authHeader() {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}


async function request(method, path, body) {
  const headers = { ...authHeader() };
  const init = { method, headers };
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body);
  }

  const res = await fetch(`${API_BASE}${path}`, init);

  if (!res.ok) {
    let detail = '';
    try {
      const data = await res.json();
      detail = data?.detail || JSON.stringify(data);
    } catch (_) {
      try {
        detail = await res.text();
      } catch (__) {
        detail = '';
      }
    }
    const err = new Error(
      `intelligence ${method} ${path} failed: ${res.status} ${detail}`
    );
    err.status = res.status;
    err.detail = detail;
    throw err;
  }

  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch (_) {
    return text;
  }
}


/**
 * List entries.
 *
 * @param {object}  opts
 * @param {string}  opts.scope     'mine' (default) | 'company'
 * @param {string[]} opts.channels subset of chat/document/negotiation/
 *                                 interview/account_note; omit for all
 * @param {boolean} opts.includeRetracted
 * @param {string}  opts.cursor    opaque `next_cursor` from the previous page
 * @param {number}  opts.limit
 */
export function listIntelligence({
  scope = 'mine', channels = null, includeRetracted = false,
  cursor = null, limit = 50,
} = {}) {
  const params = new URLSearchParams();
  params.set('scope', scope);
  if (channels && channels.length) params.set('channel', channels.join(','));
  if (includeRetracted) params.set('include_retracted', 'true');
  if (cursor != null) params.set('cursor', String(cursor));
  params.set('limit', String(limit));
  return request('GET', `/api/intelligence/entries?${params.toString()}`);
}


/**
 * Retract one entry — a SOFT delete. The record is kept; it just stops being
 * used. A 409 means this channel does not support retraction and `.detail`
 * carries the sentence explaining where it IS managed.
 */
export function retractEntry(entryId) {
  return request(
    'POST',
    `/api/intelligence/entries/${encodeURIComponent(entryId)}/retract`
  );
}


/**
 * Set (or clear) when an entry should stop counting.
 *
 * `expiresAt` is an ISO-8601 string, or null meaning NEVER EXPIRES. Null is a
 * real value here, not an omission — the field is always sent.
 */
export function setEntryExpiry(entryId, expiresAt) {
  return request(
    'PUT',
    `/api/intelligence/entries/${encodeURIComponent(entryId)}/expiry`,
    { expires_at: expiresAt ?? null }
  );
}
